"use client";
import { useState, useRef, useCallback, useEffect } from "react";

interface TimerControl {
  formattedElapsedTime: string;
  isActive: boolean;
  startTimer: () => void;
  stopTimer: () => void;
}

function useTimer(updateIntervalMs: number = 100): TimerControl {
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const stopTimer = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    setIsActive(false);
  }, []);

  const startTimer = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
    setElapsedTime(0);
    startTimeRef.current = Date.now();
    setIsActive(true);

    intervalIdRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedTime((Date.now() - startTimeRef.current) / 1000);
      }
    }, updateIntervalMs);
  }, [updateIntervalMs, stopTimer]);

  useEffect(() => {
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  const formattedElapsedTime = elapsedTime.toFixed(1);

  return {
    formattedElapsedTime,
    isActive,
    startTimer,
    stopTimer,
  };
}

export default useTimer;
